'use client';
import React, { useEffect, useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';

import cn from '@/lib/clsxm';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Card, { CardContent, CardHeader } from '@/components/@base/card';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Rating from '@/components/app/product/components/side-bar/rate-action/rating';

import localization, { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function Item({
  data,
  isActive,
  onChange,
  onCommentChange,
  validateTrigger,
  commentValue,
  setActiveItemId,
  activeItemId,
  numberOfItems,
}) {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [showError, setShowError] = useState(false);
  const [textAreaHeight, setTextAreaHeight] = useState('auto');

  useEffect(() => {
    if (validateTrigger > 0) {
      setShowError(false);
    }
  }, [validateTrigger, commentValue]);

  function handleSelect(val: number) {
    setSelectedRating(val);
    onChange && onChange(data.productId, val);
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onCommentChange && onCommentChange(data.productId, e.target.value);

    if (numberOfItems === 1) {
      e.currentTarget.style.height = 'auto';
      const newHeight = Math.min(e.currentTarget.scrollHeight, 158);
      e.currentTarget.style.height = newHeight + 'px';
      setTextAreaHeight(newHeight + 'px');
    }
  };

  return (
    <Card noShadow noBorder className='gap-1 pb-3'>
      <CardHeader>
        <Container center className='justify-between'>
          <Text size={SIZE_ENUM.SM} className='line-clamp-1 text-[#424244]'>
            {data.productTitle}
          </Text>
          <Button
            onClick={() => {
              activeItemId === data.productId
                ? setActiveItemId(undefined)
                : setActiveItemId(data.productId);
            }}
            className='h-5 w-5 rounded-[4px] border border-[#EEF0F4] bg-white! p-1.5 hover:bg-gray-100'
          >
            <IoIosArrowDown
              className={cn(
                'text-[#878F9B] transition duration-300',
                isActive ? 'rotate-180' : 'rotate-0',
              )}
            />
          </Button>
        </Container>
      </CardHeader>
      <CardContent center className='justify-between gap-3 p-0'>
        <Container center className='items-center justify-end gap-2'>
          <XImage
            className='rounded-full'
            src={data?.storeLogo?.publicLink}
            alt={data?.storeLogo?.title}
            width={28}
            height={28}
          />
          <Text className='line-clamp-1 text-[#878F9B]' size={SIZE_ENUM.XS}>
            {data.storeName}
          </Text>
        </Container>
        <Container center className='justify-start gap-1'>
          <Text className='mt-[6px] whitespace-nowrap text-[#878F9B]' size={SIZE_ENUM.XS}>
            {localization.yourRate} :
          </Text>
          <Rating onSelect={handleSelect} totalStars={5} size='text-2xl md:text-xl' />
        </Container>
      </CardContent>

      {isActive && (
        <Container className='w-full'>
          <div
            className={cn(
              'mt-2 overflow-hidden transition-all delay-200 duration-700 ease-in-out',
              isActive ? 'max-h-48' : 'max-h-0',
            )}
          >
            <textarea
              rows={4}
              placeholder={productLocalization.addYourRateAndComment}
              className={
                'no-scrollbar! w-full resize-none rounded-md border p-2 text-[10px] text-[#424244] focus:outline-none ' +
                (showError
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-[#E6E9EF] focus:border-[#FFB616]')
              }
              value={commentValue || ''}
              onChange={handleChange}
              style={{ height: textAreaHeight, maxHeight: '158px' }}
            />
          </div>
        </Container>
      )}
    </Card>
  );
}
